<template>
  <Card variant="secondary" size="medium" class="property-card">
    <Link :to="propertyLink" :decoration="false" class="property-info">
      <View gap="md" align="center">
        <PropertyPhotoPreview :image-url="property.coverImage" size="md" />

        <View justify="center" direction="column" gap="sm">
          <Text variant="h4">{{ property.displayName }}</Text>
          <PropertyStatusLabel :small="true" :status="property.status" />
        </View>
      </View>
    </Link>

    <template v-if="property?.pendingShowingRequestsCount || property.pendingEscalationsCount">
      <Divider />

      <View direction="column" gap="sm">
        <Text variant="h4">Action needed</Text>

        <Button
          v-if="property?.pendingShowingRequestsCount"
          :to="`/investor/property/${property.id}/status#showing-requests-section`"
          variant="secondary"
          size="medium"
          class="action-link"
        >
          <span>{{ pluralize('Showing request', property.pendingShowingRequestsCount, true) }}</span>
          <Icon name="chevron-right" size="extra-small" />
        </Button>

        <Button
          v-if="property?.pendingEscalationsCount"
          :to="`/investor/property/${property.id}/status#escalations-section`"
          variant="secondary"
          size="medium"
          class="action-link"
        >
          <span>{{ pluralize('Intelligent escalation', property.pendingEscalationsCount, true) }}</span>
          <Icon name="chevron-right" size="extra-small" />
        </Button>
      </View>
    </template>
  </Card>
</template>

<script setup lang="ts">
import pluralize from 'pluralize';
import { computed } from 'vue';

import { Button, Card, Divider, Icon, Link, Text, View } from '@tallo/design-system';
import { AppRouteNames } from '@tallo/investor/navigation';
import { PropertyPhotoPreview, PropertyStatus } from '@tallo/property';

import { PropertyWithPendingEventsDto } from '../dtos/property-with-pending-events';
import PropertyStatusLabel from './property-status-label.vue';

const props = defineProps<{ property: PropertyWithPendingEventsDto }>();

const propertyLink = computed(() => {
  const propertyId = props.property.id;

  switch (props.property.status) {
    case PropertyStatus.DRAFT:
      return { name: AppRouteNames.INVESTOR_PROPERTY_ONBOARDING_OVERVIEW, params: { propertyId } };

    case PropertyStatus.READY_FOR_LISTING:
      return { name: AppRouteNames.INVESTOR_PROPERTY_DETAILS, params: { propertyId } };

    default:
      return { name: AppRouteNames.INVESTOR_PROPERTY_DASHBOARD, params: { propertyId } };
  }
});
</script>

<style scoped lang="scss">
.property-card {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-md);
}

.property-info {
  flex-grow: 1;
  margin: calc(var(--t-spacing-md) * -1);
  padding: var(--t-spacing-md);
}

.action-link {
  justify-content: space-between;
}
</style>
